/**
 * Catalogue Service for ZAMMER Marketplace
 * Handles all catalogue-related API calls
 */

import api from './api';

// Create a new catalogue (draft or submitted)
export const createCatalogue = async (catalogueData) => {
  console.log(`%c[CATALOGUE-CREATE] START`, 'color: #ff6600; font-weight: bold; font-size: 14px');
  console.log(`[CATALOGUE-CREATE] Payload:`, {
    status: catalogueData.status,
    category: catalogueData.category,
    productCount: catalogueData.products?.length,
    products: catalogueData.products?.map((p, i) => ({
      index: i,
      name: p.name || '(empty)',
      zammerPrice: p.zammerPrice,
      mrp: p.mrp,
      imageCount: p.images?.length,
      imageUrls: p.images,
      variantCount: p.variants?.length
    }))
  });

  try {
    const response = await api.post('/catalogues', catalogueData);
    console.log(`%c[CATALOGUE-CREATE] SUCCESS`, 'color: #00cc00; font-weight: bold; font-size: 14px');
    console.log(`[CATALOGUE-CREATE] Response:`, {
      success: response.data?.success,
      message: response.data?.message,
      catalogueId: response.data?.data?._id,
      status: response.data?.data?.status,
      stats: response.data?.data?.stats
    });
    return response.data;
  } catch (error) {
    console.error(`%c[CATALOGUE-CREATE] FAILED`, 'color: red; font-weight: bold; font-size: 16px');
    console.error(`[CATALOGUE-CREATE] Error details:`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      serverMessage: error.response?.data?.message,
      serverError: error.response?.data?.error,
      fullResponseData: error.response?.data,
      isNetwork: !error.response && !!error.request,
      errorMessage: error.message
    });
    throw error.response?.data || { success: false, message: 'Failed to create catalogue' };
  }
};

// Save catalogue as draft
export const saveCatalogueDraft = async (catalogueData) => {
  try {
    const response = await api.post('/catalogues', { ...catalogueData, status: 'draft' });
    return response.data;
  } catch (error) {
    console.error('Save draft error:', error);
    throw error.response?.data || { success: false, message: 'Failed to save draft' };
  }
};

// Submit catalogue for processing
export const submitCatalogue = async (catalogueId) => {
  try {
    const response = await api.post(`/catalogues/${catalogueId}/submit`);
    return response.data;
  } catch (error) {
    console.error('Submit catalogue error:', error);
    throw error.response?.data || { success: false, message: 'Failed to submit catalogue' };
  }
};

// Get all catalogues for the seller
export const getSellerCatalogues = async (params = {}) => {
  try {
    const response = await api.get('/catalogues', { params });
    return response.data;
  } catch (error) {
    console.error('Get catalogues error:', error);
    throw error.response?.data || { success: false, message: 'Failed to fetch catalogues' };
  }
};

// Get a single catalogue by ID
export const getCatalogueById = async (catalogueId) => {
  try {
    const response = await api.get(`/catalogues/${catalogueId}`);
    return response.data;
  } catch (error) {
    console.error('Get catalogue error:', error);
    throw error.response?.data || { success: false, message: 'Failed to fetch catalogue' };
  }
};

// Update a draft catalogue
export const updateCatalogue = async (catalogueId, catalogueData) => {
  try {
    const response = await api.put(`/catalogues/${catalogueId}`, catalogueData);
    return response.data;
  } catch (error) {
    console.error('Update catalogue error:', error);
    throw error.response?.data || { success: false, message: 'Failed to update catalogue' };
  }
};

// Delete a catalogue
export const deleteCatalogue = async (catalogueId, deleteProducts = false) => {
  try {
    const response = await api.delete(`/catalogues/${catalogueId}`, {
      params: { deleteProducts }
    });
    return response.data;
  } catch (error) {
    console.error('Delete catalogue error:', error);
    throw error.response?.data || { success: false, message: 'Failed to delete catalogue' };
  }
};

// Upload image for catalogue product
export const uploadCatalogueImage = async (file) => {
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
  const startTime = performance.now();

  console.log(`%c[CATALOGUE-UPLOAD] ${uploadId} START`, 'color: #ff6600; font-weight: bold; font-size: 14px');
  console.log(`[CATALOGUE-UPLOAD] ${uploadId} File details:`, {
    name: file.name,
    size: `${(file.size / 1024).toFixed(1)} KB (${file.size} bytes)`,
    type: file.type,
    lastModified: new Date(file.lastModified).toISOString()
  });

  // Check auth token BEFORE uploading
  const sellerToken = localStorage.getItem('sellerToken');
  console.log(`[CATALOGUE-UPLOAD] ${uploadId} Auth check:`, {
    hasSellerToken: !!sellerToken,
    tokenLength: sellerToken?.length || 0,
    tokenPreview: sellerToken ? sellerToken.substring(0, 20) + '...' : 'MISSING'
  });

  if (!sellerToken) {
    console.error(`%c[CATALOGUE-UPLOAD] ${uploadId} FATAL: No sellerToken in localStorage!`, 'color: red; font-weight: bold; font-size: 16px');
    return { success: false, message: 'No seller authentication token found. Please log in again.' };
  }

  try {
    const formData = new FormData();
    formData.append('image', file);

    console.log(`[CATALOGUE-UPLOAD] ${uploadId} FormData created, sending POST /upload...`);
    console.log(`[CATALOGUE-UPLOAD] ${uploadId} API baseURL:`, api.defaults.baseURL);

    // DO NOT set Content-Type — let browser set it with correct multipart boundary
    const response = await api.post('/upload', formData, {
      timeout: 120000, // 2 minutes for large images via Cloudinary proxy
      headers: {
        'Content-Type': undefined // Force browser to set multipart/form-data with boundary
      }
    });

    const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
    console.log(`%c[CATALOGUE-UPLOAD] ${uploadId} SUCCESS in ${elapsed}s`, 'color: #00cc00; font-weight: bold; font-size: 14px');
    console.log(`[CATALOGUE-UPLOAD] ${uploadId} Response:`, {
      success: response.data?.success,
      url: response.data?.data?.url,
      public_id: response.data?.data?.public_id,
      format: response.data?.data?.format,
      bytes: response.data?.data?.bytes
    });

    return response.data;
  } catch (error) {
    const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
    console.error(`%c[CATALOGUE-UPLOAD] ${uploadId} FAILED after ${elapsed}s`, 'color: red; font-weight: bold; font-size: 16px');

    // Classify the error type
    const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
    const isNetwork = !error.response && !!error.request;
    const isCors = error.code === 'ERR_NETWORK' || error.message?.includes('CORS') || error.message?.includes('Network Error');
    const isAuthError = error.response?.status === 401;
    const isServerError = error.response?.status >= 500;
    const isClientError = error.response?.status >= 400 && error.response?.status < 500;

    console.error(`[CATALOGUE-UPLOAD] ${uploadId} Error classification:`, {
      isTimeout,
      isNetwork,
      isCors,
      isAuthError,
      isServerError,
      isClientError,
      errorCode: error.code,
      errorMessage: error.message
    });

    if (error.response) {
      console.error(`[CATALOGUE-UPLOAD] ${uploadId} Server response:`, {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: Object.fromEntries(
          Object.entries(error.response.headers || {}).filter(([k]) =>
            ['content-type', 'x-request-id', 'x-powered-by'].includes(k.toLowerCase())
          )
        )
      });
    } else if (error.request) {
      console.error(`[CATALOGUE-UPLOAD] ${uploadId} No response received (network/CORS issue):`, {
        readyState: error.request.readyState,
        status: error.request.status,
        responseURL: error.request.responseURL
      });
    } else {
      console.error(`[CATALOGUE-UPLOAD] ${uploadId} Request setup error:`, error.message);
    }

    const errorResult = error.response?.data || { success: false, message: error.message || 'Failed to upload image' };
    console.error(`[CATALOGUE-UPLOAD] ${uploadId} Returning error result:`, errorResult);
    return errorResult;
  }
};

// Helper function to prepare catalogue data for submission
export const prepareCatalogueData = (category, products, options = {}) => {
  return {
    category: {
      level1: category.level1,
      level2: category.level2,
      level3: category.level3,
      level4: category.level4 || '',
      path: category.path || `${category.level1} > ${category.level2} > ${category.level3}${category.level4 ? ' > ' + category.level4 : ''}`
    },
    products: products.map(product => ({
      name: product.name,
      description: product.description || '',
      zammerPrice: Number(product.zammerPrice),
      mrp: Number(product.mrp),
      images: product.images || [],
      variants: product.variants || [{ size: 'M', color: 'Black', colorCode: '#000000', quantity: 1 }],
      fabricType: product.fabricType || ''
    })),
    gst: options.gst || '',
    hsnCode: options.hsnCode || '',
    name: options.name || `Catalogue ${new Date().toLocaleDateString()}`,
    status: options.status || 'draft'
  };
};

export default {
  createCatalogue,
  saveCatalogueDraft,
  submitCatalogue,
  getSellerCatalogues,
  getCatalogueById,
  updateCatalogue,
  deleteCatalogue,
  uploadCatalogueImage,
  prepareCatalogueData
};
